import { useNavigate, useParams } from '@tanstack/react-router'
import type { DealFlowArchiveItem } from '@workspace/api-backend'
import { formatAmount, raiseStageLabel, stageLabel } from '@workspace/seedforge-shared'
import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { FullScreenState } from '@workspace/ui/components/full-screen-state'
import { cn, getInitials } from '@workspace/ui/lib/utils'
import { ArrowLeft, ArrowRight, ChevronRight, ThumbsDown } from 'lucide-react'
import { InvestorLayout } from '../../components/investor-layout'
import { useDealFlowArchiveQuery } from '../../hooks/api'

function formatDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

function KindBadge({ kind }: { kind: 'PASS' | 'CONNECT' }) {
  const isPass = kind === 'PASS'
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest',
        isPass
          ? 'border-red-500/40 bg-red-500/10 text-red-300'
          : 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
      )}
    >
      {isPass ? <ThumbsDown className="size-3" /> : <ArrowRight className="size-3" />}
      {isPass ? 'Passed' : 'Connected'}
    </span>
  )
}

function ArchiveCard({ item, investorId }: { item: DealFlowArchiveItem; investorId: string }) {
  const navigate = useNavigate()
  const profile = item.match.profile.startup
  const startupId = item.match.profile.startupId

  const metaParts = [
    profile.categoryName,
    stageLabel(profile.productStage) || null,
    profile.location,
    profile.fundraisingStage &&
      [raiseStageLabel(profile.fundraisingStage), formatAmount(profile.fundraisingAmount) || null]
        .filter(Boolean)
        .join(' '),
  ].filter(Boolean) as string[]

  const handleOpen = () =>
    navigate({
      to: '/app/investor/$id/deal-flow/$startupId',
      params: { id: investorId, startupId },
    })

  return (
    <button
      type="button"
      onClick={handleOpen}
      aria-label={`View ${profile.name} profile`}
      className="block w-full cursor-pointer rounded-xl border border-border/60 bg-card/30 px-5 py-4 text-left transition-colors hover:border-border hover:bg-card/50"
    >
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-md border border-border/60 bg-card text-xs font-semibold text-muted-foreground">
            {getInitials(profile.name)}
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="truncate text-sm font-semibold text-foreground">{profile.name}</h3>
            {metaParts.length > 0 && (
              <p className="truncate text-xs text-muted-foreground">
                {metaParts.map((part, index) => (
                  <span key={part}>
                    {index > 0 && <span className="text-muted-foreground/50"> · </span>}
                    {part}
                  </span>
                ))}
              </p>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-3">
            <KindBadge kind={item.kind} />
            <span className="text-xs text-muted-foreground/70">
              {formatDate(item.interactedAt)}
            </span>
          </div>
        </div>

        {item.kind === 'PASS' && item.reasons.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {item.reasons.map((r) => (
              <span
                key={r}
                className="rounded border border-border/50 bg-muted/40 px-2 py-0.5 text-xs text-muted-foreground"
              >
                {r}
              </span>
            ))}
          </div>
        )}

        {item.note && (
          <p className="rounded-lg border border-border/40 bg-muted/20 px-3.5 py-2.5 text-xs leading-relaxed text-muted-foreground">
            {item.note}
          </p>
        )}

        {item.message && (
          <p className="rounded-lg border border-emerald-500/20 bg-emerald-500/4 px-3.5 py-2.5 text-xs leading-relaxed text-muted-foreground">
            {item.message}
          </p>
        )}

        <div className="flex items-center justify-end border-t border-border/40 pt-3 text-xs text-muted-foreground/80">
          View profile
          <ChevronRight className="ml-1 size-3.5" />
        </div>
      </div>
    </button>
  )
}

export function DealFlowArchivePage() {
  const { id } = useParams({ from: '/app/investor/$id/deal-flow_/archive' })
  const navigate = useNavigate()
  const archiveQuery = useDealFlowArchiveQuery(id)

  const goBack = () => navigate({ to: '/app/investor/$id/deal-flow', params: { id } })

  if (archiveQuery.isLoading) {
    return (
      <InvestorLayout activeNav="Deal Flow">
        <FullScreenState loading title="Loading archive..." />
      </InvestorLayout>
    )
  }

  const items = archiveQuery.data?.data ?? []

  return (
    <InvestorLayout activeNav="Deal Flow">
      <div className="mx-auto max-w-6xl py-2">
        <SecondaryButton size="sm" onClick={goBack} className="mb-4">
          <ArrowLeft className="size-3.5" />
          Back to Deal Flow
        </SecondaryButton>

        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">Archive</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Startups you've passed on or connected with
          </p>
        </div>

        {items.length === 0 ? (
          <div className="mt-16 flex flex-col items-center text-center">
            <p className="text-sm font-medium text-foreground">No archived startups yet.</p>
            <p className="mt-1 max-w-md text-xs text-muted-foreground">
              When you pass on or connect with a startup, it will appear here.
            </p>
          </div>
        ) : (
          <div className="mt-8 space-y-3">
            {items.map((item) => (
              <ArchiveCard key={item.match.profile.startupId} item={item} investorId={id} />
            ))}
          </div>
        )}
      </div>
    </InvestorLayout>
  )
}
